"use client";

import { useState } from "react";
import {
  CopyButton,
  DataTable,
  ErrorNotice,
  Notice,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  RESULT_CARD,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  type NumberFormatter,
  plain,
  sig,
  toTsv,
} from "@/lib/tool-kit/biology-lab/format";
import {
  type BpMass,
  type LigationError,
  type LigationInput,
  type LigationResult,
  MAX_TOTAL_NG_PER_UL,
  MAX_VECTOR_FMOL,
  MIN_TOTAL_NG_PER_UL,
  MIN_VECTOR_FMOL,
  planLigation,
} from "@/lib/tool-kit/biology-lab/ligation";
import { parseMeasuredNumber } from "@/lib/tool-kit/math/number-input";

/** English strings. A translated content file overrides these through `ui`. */
export const LIGATION_CALCULATOR_STRINGS = {
  vectorLength: "Vector length (bp)",
  vectorMass: "Vector amount (ng)",
  vectorMassHint: "NEB's standard reaction uses 50 ng.",
  insertLength: "Insert length (bp)",
  ratio: "Molar ratio — insert parts per 1 part vector",
  ratioHint: "3 means a 1:3 vector:insert ligation.",
  vectorConc: "Vector stock (ng/µL, optional)",
  insertConc: "Insert stock (ng/µL, optional)",
  concHint: "Add both stocks to get pipetting volumes and water.",
  totalVolume: "Reaction volume (µL)",
  ligaseVolume: "T4 DNA ligase (µL)",
  bpMassLabel: "Average mass of a base pair (for pmol only)",
  bpMass660: "660 g/mol (common convention)",
  bpMass618: "617.96 g/mol (NEB's published average, default)",
  results: "Results",
  insertMassLabel: "Insert to add",
  unitNg: "ng",
  vectorAmount: "Vector",
  insertAmount: "Insert",
  unitFmol: "fmol",
  totalConcentration: "Total DNA in the reaction",
  unitNgUl: "ng/µL",
  working: "The working",
  workingMass:
    "insert (ng) = ratio × vector (ng) × insert length ÷ vector length = {ratio} × {vector} × {insertLength} ÷ {vectorLength} = {result} ng",
  recipeHeading: "Reaction recipe",
  colComponent: "Component",
  colAmount: "Amount",
  colVolume: "Volume (µL)",
  rowBuffer: "10X T4 DNA ligase buffer",
  rowVector: "Vector DNA",
  rowInsert: "Insert DNA",
  rowWater: "Nuclease-free water",
  rowLigase: "T4 DNA ligase (add last)",
  rowTotal: "Total",
  amountNg: "{ng} ng ({fmol} fmol)",
  noVolume: "—",
  toVolume: "to {total}",
  copyRecipe: "Copy recipe",
  copied: "Copied!",
  needStocks:
    "Enter the stock concentrations of the vector and the insert to fill in the volumes and the water.",
  overfull:
    "The buffer, ligase and DNA add up to more than {total} µL, so there is no room for water. Use a more concentrated stock or a larger reaction volume.",
  smallVolume:
    "A DNA volume under 0.5 µL is hard to pipette accurately. Dilute that stock first and enter the diluted concentration.",
  lowConcentration:
    "At {value} ng/µL the DNA is more dilute than the 1–10 ng/µL NEB recommends, which favours the vector closing on itself. Use a smaller volume or more DNA.",
  highConcentration:
    "At {value} ng/µL the DNA is more concentrated than the 1–10 ng/µL NEB recommends. Increase the reaction volume.",
  vectorOutside:
    "NEB suggests 20–30 fmol of vector; this reaction has {value} fmol.",
  ratioOutside:
    "NEB calls vector:insert ratios from 1:1 to 1:10 optimal for a single insert (up to 1:20 for short adaptors).",
  errVectorLength: "Enter a vector length greater than 0.",
  errInsertLength: "Enter an insert length greater than 0.",
  errVectorMass: "Enter a vector amount greater than 0 ng.",
  errRatio: "Enter a molar ratio greater than 0.",
  errVolume:
    "Enter a reaction volume greater than 0 µL and a ligase volume of 0 or more.",
  errBuffer: "The buffer stock must be at least 1X.",
  enter: "Enter the vector and insert lengths and the vector amount.",
  extraNote:
    "For a vector-only control, make the same reaction with water in place of the insert.",
} as const;

type Strings = typeof LIGATION_CALCULATOR_STRINGS;

const ID = "lig";
const BUFFER_STOCK = 10;
const SMALL_VOLUME_UL = 0.5;
const FMOL_PER_PMOL = 1000;
const GRID = "grid gap-4 sm:grid-cols-2 lg:grid-cols-3";

function errorText(code: LigationError, strings: Strings): string {
  const messages: Record<LigationError, string> = {
    vectorLength: strings.errVectorLength,
    insertLength: strings.errInsertLength,
    vectorMass: strings.errVectorMass,
    ratio: strings.errRatio,
    volume: strings.errVolume,
    buffer: strings.errBuffer,
  };
  return messages[code];
}

function volumeCell(
  value: number | null,
  fmt: NumberFormatter,
  strings: Strings
): string {
  return value === null ? strings.noVolume : sig(fmt, value, 3);
}

function recipeRows(
  r: LigationResult,
  input: LigationInput,
  strings: Strings,
  fmt: NumberFormatter
): string[][] {
  const fmol = (pmol: number) => sig(fmt, pmol * FMOL_PER_PMOL, 3);
  const { recipe } = r;
  return [
    [strings.rowBuffer, "", volumeCell(recipe.bufferUl, fmt, strings)],
    [
      `${strings.rowVector} (${plain(fmt, input.vectorLength)} bp)`,
      t(strings.amountNg, {
        ng: sig(fmt, input.vectorNg, 4),
        fmol: fmol(r.vectorPmol),
      }),
      volumeCell(recipe.vectorUl, fmt, strings),
    ],
    [
      `${strings.rowInsert} (${plain(fmt, input.insertLength)} bp)`,
      t(strings.amountNg, {
        ng: sig(fmt, r.insertNg, 4),
        fmol: fmol(r.insertPmol),
      }),
      volumeCell(recipe.insertUl, fmt, strings),
    ],
    [
      strings.rowWater,
      "",
      recipe.waterUl === null
        ? t(strings.toVolume, { total: plain(fmt, input.totalUl) })
        : volumeCell(recipe.waterUl, fmt, strings),
    ],
    [strings.rowLigase, "", volumeCell(recipe.ligaseUl, fmt, strings)],
    [strings.rowTotal, "", plain(fmt, input.totalUl)],
  ];
}

function warnings(
  r: LigationResult,
  input: LigationInput,
  strings: Strings,
  fmt: NumberFormatter
): string[] {
  const out: string[] = [];
  if (r.overfull) {
    out.push(t(strings.overfull, { total: plain(fmt, input.totalUl) }));
  }
  const small = [r.recipe.vectorUl, r.recipe.insertUl].some(
    (v) => v !== null && v < SMALL_VOLUME_UL
  );
  if (small) {
    out.push(strings.smallVolume);
  }
  const value = sig(fmt, r.totalNgPerUl, 3);
  if (r.totalNgPerUl < MIN_TOTAL_NG_PER_UL) {
    out.push(t(strings.lowConcentration, { value }));
  } else if (r.totalNgPerUl > MAX_TOTAL_NG_PER_UL) {
    out.push(t(strings.highConcentration, { value }));
  }
  const vectorFmol = r.vectorPmol * FMOL_PER_PMOL;
  if (vectorFmol < MIN_VECTOR_FMOL || vectorFmol > MAX_VECTOR_FMOL) {
    out.push(t(strings.vectorOutside, { value: sig(fmt, vectorFmol, 3) }));
  }
  if (input.ratio < 1 || input.ratio > 10) {
    out.push(strings.ratioOutside);
  }
  return out;
}

function Results({
  input,
  fmt,
}: {
  input: LigationInput;
  fmt: NumberFormatter;
}) {
  const strings = useToolUi(LIGATION_CALCULATOR_STRINGS);
  const outcome = planLigation(input);
  if ("error" in outcome) {
    return <ErrorNotice>{errorText(outcome.error, strings)}</ErrorNotice>;
  }
  const r = outcome.result;
  const head = [strings.colComponent, strings.colAmount, strings.colVolume];
  const rows = recipeRows(r, input, strings, fmt);
  const notes = warnings(r, input, strings, fmt);
  const stocksMissing = r.recipe.waterUl === null;
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={`${strings.insertMassLabel} (${strings.unitNg})`}
          value={sig(fmt, r.insertNg, 4)}
        />
        <StatCard
          label={`${strings.vectorAmount} (${strings.unitFmol})`}
          value={sig(fmt, r.vectorPmol * FMOL_PER_PMOL, 4)}
        />
        <StatCard
          label={`${strings.insertAmount} (${strings.unitFmol})`}
          value={sig(fmt, r.insertPmol * FMOL_PER_PMOL, 4)}
        />
      </StatGrid>
      <p className="text-muted-foreground text-sm">
        {strings.totalConcentration}: {sig(fmt, r.totalNgPerUl, 3)}{" "}
        {strings.unitNgUl}
      </p>
      <div className="space-y-2 rounded-lg border border-border bg-muted/30 p-4 font-mono text-muted-foreground text-sm leading-relaxed">
        <p className="font-medium font-sans text-foreground">
          {strings.working}
        </p>
        <p>
          {t(strings.workingMass, {
            ratio: plain(fmt, input.ratio),
            vector: plain(fmt, input.vectorNg),
            insertLength: plain(fmt, input.insertLength),
            vectorLength: plain(fmt, input.vectorLength),
            result: sig(fmt, r.insertNg, 4),
          })}
        </p>
      </div>
      <h3 className={WIDGET_H3}>{strings.recipeHeading}</h3>
      <DataTable head={head} rows={rows} />
      {stocksMissing ? <Notice>{strings.needStocks}</Notice> : null}
      {notes.map((note) => (
        <Notice key={note}>{note}</Notice>
      ))}
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copyRecipe}
        small
        text={toTsv(head, rows)}
      />
      <p className="text-muted-foreground text-xs">{strings.extraNote}</p>
    </>
  );
}

export default function LigationCalculatorWidget() {
  const strings = useToolUi(LIGATION_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [vectorLength, setVectorLength] = useState("4000");
  const [vectorNg, setVectorNg] = useState("50");
  const [insertLength, setInsertLength] = useState("1000");
  const [ratio, setRatio] = useState("3");
  const [vectorConc, setVectorConc] = useState("100");
  const [insertConc, setInsertConc] = useState("50");
  const [totalUl, setTotalUl] = useState("20");
  const [ligaseUl, setLigaseUl] = useState("1");
  const [bpMass, setBpMass] = useState<BpMass>("617.96");

  const vLen = parseMeasuredNumber(vectorLength);
  const iLen = parseMeasuredNumber(insertLength);
  const vNg = parseMeasuredNumber(vectorNg);
  const ratioValue = parseMeasuredNumber(ratio);
  const total = parseMeasuredNumber(totalUl);
  const ligase = parseMeasuredNumber(ligaseUl);
  const ready =
    vLen !== null &&
    iLen !== null &&
    vNg !== null &&
    ratioValue !== null &&
    total !== null &&
    ligase !== null;
  const input: LigationInput | null = ready
    ? {
        vectorLength: vLen,
        vectorNg: vNg,
        insertLength: iLen,
        ratio: ratioValue,
        bpMass,
        vectorConc: parseMeasuredNumber(vectorConc),
        insertConc: parseMeasuredNumber(insertConc),
        totalUl: total,
        ligaseUl: ligase,
        bufferStock: BUFFER_STOCK,
      }
    : null;

  return (
    <>
      <div className={GRID}>
        <TextField
          id={`${ID}-vlen`}
          label={strings.vectorLength}
          numeric
          onChange={setVectorLength}
          value={vectorLength}
        />
        <TextField
          hint={strings.vectorMassHint}
          id={`${ID}-vng`}
          label={strings.vectorMass}
          numeric
          onChange={setVectorNg}
          value={vectorNg}
        />
        <TextField
          id={`${ID}-ilen`}
          label={strings.insertLength}
          numeric
          onChange={setInsertLength}
          value={insertLength}
        />
        <TextField
          hint={strings.ratioHint}
          id={`${ID}-ratio`}
          label={strings.ratio}
          numeric
          onChange={setRatio}
          value={ratio}
        />
        <TextField
          id={`${ID}-vconc`}
          label={strings.vectorConc}
          numeric
          onChange={setVectorConc}
          value={vectorConc}
        />
        <TextField
          hint={strings.concHint}
          id={`${ID}-iconc`}
          label={strings.insertConc}
          numeric
          onChange={setInsertConc}
          value={insertConc}
        />
        <TextField
          id={`${ID}-total`}
          label={strings.totalVolume}
          numeric
          onChange={setTotalUl}
          value={totalUl}
        />
        <TextField
          id={`${ID}-ligase`}
          label={strings.ligaseVolume}
          numeric
          onChange={setLigaseUl}
          value={ligaseUl}
        />
        <SelectField
          id={`${ID}-bp`}
          label={strings.bpMassLabel}
          onChange={setBpMass}
          options={[
            { value: "617.96", label: strings.bpMass618 },
            { value: "660", label: strings.bpMass660 },
          ]}
          value={bpMass}
        />
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {input ? (
          <div className={`${RESULT_CARD} space-y-4`}>
            <Results fmt={fmt} input={input} />
          </div>
        ) : (
          <Notice>{strings.enter}</Notice>
        )}
      </section>
    </>
  );
}
